import { useEffect, useState, useCallback, useRef } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { auth, consumeRedirect } from './firebase'
import { useStore } from './store'
import { Sidebar } from './components/Sidebar'
import { NoteList } from './components/NoteList'
import { Editor } from './components/Editor'
import { History } from './components/History'
import { inTextEntry, isUndoChord } from './lib/keys'
import { TodoView } from './components/TodoView'
import { Landing } from './components/Landing'
import { VerifyEmail } from './components/VerifyEmail'
import { ConfirmDialog } from './components/ConfirmDialog'
import { Splash } from './components/Splash'
import { backAction } from './lib/back'

type Pane = 'folders' | 'list' | 'editor'

function useIsNarrow() {
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 820px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)')
    const on = (e: MediaQueryListEvent) => setNarrow(e.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return narrow
}

export default function App() {
  const store = useStore()
  const narrow = useIsNarrow()
  const [pane, setPane] = useState<Pane>('list')
  const appRef = useRef<HTMLDivElement>(null)

  /**
   * Move to a pane and start its slide in the same tick.
   *
   * Setting state alone left ~94ms between the tap and the first pixel of
   * movement on a phone — React re-renders, then the style change lands, then
   * the transition starts. Writing the attribute directly starts the
   * compositor immediately; the state update that follows sets the same value,
   * so React stays the source of truth and nothing flickers.
   */
  const goToPane = useCallback((next: Pane) => {
    appRef.current?.setAttribute('data-pane', next)
    setPane(next)
  }, [])
  const [sidebarOpen, setSidebarOpen] = useState(true)

  // CSS needs to know which pane is leftmost so the macOS traffic lights get
  // their inset there rather than on a hidden sidebar.
  useEffect(() => {
    document.body.setAttribute('data-sidebar', sidebarOpen && !narrow ? 'open' : 'closed')
  }, [sidebarOpen, narrow])

  useEffect(() => {
    void consumeRedirect()
    document.body.setAttribute('data-focus-pane', 'list')
    return onAuthStateChanged(auth, (u) => store.setUser(u))
    // Subscribing once at mount is deliberate: setUser tears down old listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // "Open Note in New Window" passes the note through the query string.
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get('note')
    if (!wanted || !store.loaded) return
    if (store.notes.some((n) => n.id === wanted)) {
      store.select(wanted)
      goToPane('editor')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.loaded])

  // Window focus drives the note list's selection colour, exactly like AppKit.
  useEffect(() => {
    const on = () => document.body.classList.add('window-focused')
    const off = () => document.body.classList.remove('window-focused')
    on()
    window.addEventListener('focus', on)
    window.addEventListener('blur', off)
    return () => { window.removeEventListener('focus', on); window.removeEventListener('blur', off) }
  }, [])

  /**
   * Android's back gesture and button. Without a listener Capacitor closes the
   * app, so swiping back from inside a note quit rather than returning to the
   * list. The same handler drives the browser's Back button on the web.
   */
  useEffect(() => {
    let detach: (() => void) | undefined
    const handle = () => {
      const overlayOpen = !!document.querySelector(
        '.ctx-menu, .auth-scrim, .sheet-scrim, .confirm-scrim, .popover, .history-panel',
      )
      const action = backAction({ pane, wide: !narrow, overlayOpen })
      if (action.kind === 'close-overlay') {
        // Let whatever is open dismiss itself the way Escape would.
        window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
        return true
      }
      if (action.kind === 'go') { goToPane(action.pane); return true }
      return false
    }

    void import('@capacitor/app').then(({ App: CapApp }) => {
      const sub = CapApp.addListener('backButton', () => {
        if (!handle()) void CapApp.exitApp()
      })
      detach = () => { void sub.then((h) => h.remove()) }
    }).catch(() => undefined)

    // The browser's own Back button, which had the same gap.
    const onPop = () => { if (handle()) history.pushState(null, '') }
    history.pushState(null, '')
    window.addEventListener('popstate', onPop)
    return () => { detach?.(); window.removeEventListener('popstate', onPop) }
  }, [pane, narrow])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey
      if (!mod) return
      if (e.key.toLowerCase() === 'n' && !e.shiftKey) {
        e.preventDefault(); store.newNote(); goToPane('editor')
      }
      if (e.key.toLowerCase() === 'f') {
        e.preventDefault()
        document.querySelector<HTMLInputElement>('.search input')?.focus()
      }
      // ⌘Z / Ctrl+Z brings back the last note you deleted.
      if (isUndoChord(e) && !inTextEntry(e.target)) {
        e.preventDefault()
        store.undo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [store])

  if (!store.authReady) return <Splash message="Signing you in…" />
  if (!store.user) return <Landing />
  // The rules require email_verified, so an unconfirmed account can't read or
  // write anything — say so rather than letting every action fail silently.
  if (!store.user.emailVerified) return <VerifyEmail />
  // Signed in, but the first sync has not landed. On a cold mobile connection
  // this is the long part, and it used to show an empty list as though the
  // account had no notes at all.
  if (!store.loaded) return <Splash message="Loading your notes…" />

  return (
    <div ref={appRef} className="app" data-pane={narrow ? pane : undefined}>
      {(sidebarOpen || narrow) && (
        <Sidebar onToggle={() => setSidebarOpen(false)} onPick={() => narrow && goToPane('list')} />
      )}

      {store.scope.kind === 'todo' ? (
        <TodoView
          onBack={() => goToPane('folders')}
          showSidebarToggle={!sidebarOpen && !narrow}
          onShowSidebar={() => setSidebarOpen(true)}
        />
      ) : (
        <>
          <NoteList
            onBack={() => goToPane('folders')}
            onOpen={() => goToPane('editor')}
            showSidebarToggle={!sidebarOpen && !narrow}
            onShowSidebar={() => setSidebarOpen(true)}
          />
          <Editor onBack={() => goToPane('list')} />
          <History />
        </>
      )}

      <ConfirmDialog />
    </div>
  )
}
